import PropTypes from 'prop-types';
import React from 'react';

import { useTranslation } from 'react-i18next';

import { ToastContext, WeezHeader, Card } from '@weezevent/nacre';

import Event from '../../models/eventModel';
import { default as SubscriptionModel } from '../../models/subscription';

import SubscriptionLinkModal from './linkModal';
import Loading from '../../components/loading';
import DeletionModal from '../../components/deletionModal';
import ListEvent from './listeEvent';
import SubscriptionLink from '../../models/subscriptionLink';
import { useHistory } from 'react-router-dom';

export default function Subscription({ organizationId, subscriptionId }) {
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const history = useHistory();
    const [state, setState] = React.useState({
        currentResetLink: null,
        currentUnlockLink: null,
        currentRemoveLink: null,
        currentSubscriptionLink: null
    });

    const [subscription, loadingSubscription] = SubscriptionModel.useApiModel(
        {
            id: subscriptionId,
            organizationId
        },
        [subscriptionId, organizationId]
    );

    // just used to check if subscription has subscriptionLink active
    const [subscriptionLink, loadingLinks, sync] = SubscriptionLink.useApiModel(
        {
            organizationId,
            subscriptionId,
            query: { limit: 100, offset: 0 }
        },
        [subscriptionId, organizationId]
    );

    const [events, loadingEvent] = Event.useApiModel(
        {
            organizationId,
            query: {
                publication_status__in: 'published,draft'
            }
        },
        [organizationId]
    );

    const unlinkSubscriptionLink = React.useCallback(
        link => {
            link.unlink({ organizationId })
                .then(obj => {
                    toast.success(t('ticket.webservice.subscriptionLink.unlinkSuccess'));
                    setState({ currentResetLink: null, currentRemoveLink: null, currentSubscriptionLink: null, currentUnlockLink: null });
                    sync({ allow_cache: false, cache: false });
                })
                .catch(e => {
                    toast.error(t('ticket.webservice.subscriptionLink.unlinkFailure'));
                });
        },
        [organizationId, sync, toast, setState]
    );

    const unlockSubscriptionLink = React.useCallback(
        link => {
            link.unlock({ organizationId })
                .then(obj => {
                    toast.success(t('ticket.webservice.subscriptionLink.unlockSuccess'));
                    sync({ allow_cache: false, cache: false });
                })
                .catch(e => {
                    toast.error(t('ticket.webservice.subscriptionLink.unlockFailure'));
                })
                .finally(e => {
                    setState({ currentResetLink: null, currentRemoveLink: null, currentSubscriptionLink: null, currentUnlockLink: null });
                });
        },
        [organizationId, sync, toast, setState]
    );

    const resetSubscriptionLink = React.useCallback(
        link => {
            link.reset({ organizationId })
                .then(obj => {
                    toast.success(t('ticket.webservice.subscriptionLink.resetSuccess'));
                    setState({ currentResetLink: null, currentRemoveLink: null, currentSubscriptionLink: null, currentUnlockLink: null });
                    sync({ allow_cache: false, cache: false });
                })
                .catch(e => {
                    toast.error(t('ticket.webservice.subscriptionLink.resetFailure'));
                });
        },
        [organizationId, sync, toast, setState]
    );

    const renderLinks = React.useMemo(() => {
        if (!subscription || subscription.status === 'login_error') {
            return;
        }

        if (!subscriptionLink || subscriptionLink.length === 0) {
            return t('ticket.webservice.subscription.noLink');
        }

        return <ListEvent setState={e => setState(e)} organizationId={organizationId} subscriptionId={subscriptionId} />;
    }, [subscriptionLink, subscription]);

    if (loadingSubscription || loadingLinks || loadingEvent) {
        return <Loading />;
    }

    return (
        <div>
            <DeletionModal
                // RESET MODAL
                objectToDelete={state.currentResetLink}
                onClose={() => setState({ currentResetLink: null, currentRemoveLink: null, currentSubscriptionLink: null, currentUnlockLink: null })}
                onAccept={() => resetSubscriptionLink(state.currentResetLink)}
                labelTitle={t('ticket.webservice.subscriptionLink.resetTitleModal')}
                labelButton={t('ticket.common.action.reset')}
                labelBody={t('ticket.webservice.subscriptionLink.resetBodyModal')}
                icon={'sync'}
            />
            <DeletionModal
                // DELETE MODAL
                objectToDelete={state.currentRemoveLink}
                onClose={() => setState({ currentResetLink: null, currentRemoveLink: null, currentSubscriptionLink: null, currentUnlockLink: null })}
                onAccept={() => unlinkSubscriptionLink(state.currentRemoveLink)}
                labelTitle={t('ticket.webservice.list.modalRemove.title')}
                labelButton={t('ticket.common.action.delete')}
                labelBody={t('ticket.webservice.list.modalRemove.body')}
                icon={'sync'}
            />
            <DeletionModal
                // UNLOCK MODAL
                objectToDelete={state.currentUnlockLink}
                onClose={() => setState({ currentResetLink: null, currentRemoveLink: null, currentSubscriptionLink: null, currentUnlockLink: null })}
                onAccept={() => unlockSubscriptionLink(state.currentUnlockLink)}
                labelTitle={t('ticket.webservice.list.modalUnlock.title')}
                labelButton={t('ticket.common.action.continue')}
                labelBody={t('ticket.webservice.list.modalUnlock.body')}
                icon={'sync'}
            />
            <SubscriptionLinkModal
                events={events}
                organizationId={organizationId}
                onError={() => toast.error(t('ticket.webservice.subscriptionLink.error'))}
                subscriptionLink={state.currentSubscriptionLink}
                onClose={() => setState({ currentResetLink: null, currentRemoveLink: null, currentSubscriptionLink: null })}
                onSuccess={obj => {
                    toast.success(t('ticket.webservice.subscriptionLink.linked'));
                    setState({ currentResetLink: null, currentRemoveLink: null, currentSubscriptionLink: null });
                    sync({ allow_cache: false, cache: false });
                }}
            />
            <WeezHeader
                title={`${t('ticket.webservice.distributor')} : ` + subscription.type}
                backLink={{ onClick: () => history.push(`/ticket/O${organizationId}/webservices`) }}
            />
            <Card>{renderLinks}</Card>
        </div>
    );
}

Subscription.propTypes = {
    subscriptionId: PropTypes.string,
    organizationId: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired
};
